import { useEffect } from 'react';
import {
    Mail, Globe, ChevronDown,
    Sparkles, Clock, Headphones, Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import PublicPageHeader from '@components/common/PublicPageHeader';
import { SEOHead, SEO_META_TAGS, useSEO } from '@/seo';

const Contact = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-cosmic-950 text-purple-50 font-sans selection:bg-purple-900 selection:text-white relative">
            <SEOHead {...useSEO(SEO_META_TAGS.contact.English, 'English')} />
            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-32 md:pb-24 px-4 sm:px-6 font-sans">
                <div className="container mx-auto max-w-6xl">

                    <PublicPageHeader
                        title="Contact Us"
                        highlightedText="Us"
                        description="Get in touch with our support team for assistance, feedback, or any inquiries about our services."
                        icon={Headphones}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-24">
                        {/* Info Cards */}
                        <div
                            className="bg-white/[0.08] border border-white/20 p-10 rounded-[40px] group hover:border-purple-400/50 transition-colors duration-500 shadow-[0_20px_50px_rgba(0,0,0,0.3)]"
                        >
                            <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-400 mb-8 shadow-[0_0_20px_rgba(147,51,234,0.1)] group-hover:bg-purple-500/20 transition-colors">
                                <Mail size={32} />
                            </div>
                            <h3 className="text-3xl font-bold mb-4 font-serif text-white tracking-tight">Email Support</h3>
                            <p className="text-purple-300 font-sans font-black mb-8 text-xl tracking-tight">support@bhrigugpt.com</p>
                            <p className="text-purple-200/70 text-xs font-bold uppercase tracking-widest font-sans">We respond within 24 hours</p>
                        </div>

                        <div
                            className="bg-white/[0.08] border border-white/20 p-10 rounded-[40px] group hover:border-purple-400/50 transition-colors duration-500 shadow-[0_20px_50px_rgba(0,0,0,0.3)]"
                        >
                            <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-400 mb-8 shadow-[0_0_20px_rgba(147,51,234,0.1)] group-hover:bg-purple-500/20 transition-colors">
                                <Clock size={32} />
                            </div>
                            <h3 className="text-3xl font-bold mb-6 font-serif text-white tracking-tight">Always Available</h3>
                            <div className="space-y-6 font-sans text-center">
                                <div className="bg-white/[0.04] p-6 rounded-2xl border border-purple-500/20">
                                    <p className="text-6xl font-black text-white mb-2">24/7</p>
                                    <p className="text-purple-200/70 text-sm uppercase tracking-widest font-bold">Every Day</p>
                                </div>
                                <p className="text-purple-100/80 text-sm leading-relaxed">
                                    We're here for you round the clock, every day of the year.
                                </p>
                            </div>
                        </div>

                        <div
                            className="bg-white/[0.08] border border-white/20 p-10 rounded-[40px] group hover:border-purple-400/50 transition-colors duration-500 shadow-[0_20px_50px_rgba(0,0,0,0.3)]"
                        >
                            <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-400 mb-8 shadow-[0_0_20px_rgba(147,51,234,0.1)] group-hover:bg-purple-500/20 transition-colors">
                                <Globe size={32} />
                            </div>
                            <h3 className="text-3xl font-bold mb-4 font-serif text-white tracking-tight">Language Support</h3>
                            <p className="text-purple-100/80 font-sans text-base mb-8 leading-relaxed">
                                Reach out to us in your preferred language for personalized assistance.
                            </p>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-sm text-purple-200 font-bold font-sans bg-purple-500/5 p-4 rounded-2xl border border-purple-500/10">
                                    <div className="w-2 h-2 bg-purple-400 rounded-full" />
                                    <span>English</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-purple-200 font-bold font-sans bg-purple-500/5 p-4 rounded-2xl border border-purple-500/10">
                                    <div className="w-2 h-2 bg-purple-400 rounded-full" />
                                    <span>हिंदी (Hindi)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Help & CTA */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-stretch mb-24 border-t border-white/20 pt-24">
                        <div className="space-y-12">
                            <h2 className="text-4xl md:text-5xl font-black font-serif tracking-tight text-white leading-tight">Quick Help</h2>
                            <div className="grid gap-6">
                                {[
                                    { name: 'Terms of Service', path: '/terms', desc: 'Rules and celestial protocols' },
                                    { name: 'Privacy Policy', path: '/privacy', desc: 'Data security and stewardship' },
                                    { name: 'Cancellations & Refunds', path: '/refunds', desc: 'Processing cycle and eligibility' }
                                ].map((link, i) => (
                                    <Link
                                        key={i} to={link.path}
                                        className="flex items-center justify-between p-8 bg-white/[0.08] border border-white/20 rounded-[32px] hover:bg-white/[0.12] hover:border-purple-400/50 transition-colors group"
                                    >
                                        <div className="space-y-1">
                                            <span className="font-serif text-white font-bold text-2xl tracking-tight block">{link.name}</span>
                                            <span className="text-purple-200/70 text-xs font-sans uppercase tracking-widest">{link.desc}</span>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-colors -rotate-90">
                                            <ChevronDown size={24} className="text-purple-400" />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white/[0.08] p-16 rounded-[64px] border border-purple-500/20 text-center relative overflow-hidden group flex flex-col justify-center">
                            <div className="absolute inset-0 bg-white/[0.01]" />
                            <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.06] transition-colors duration-1000 -rotate-12 group-hover:rotate-0">
                                <Sparkles size={180} className="text-purple-500" />
                            </div>
                            <div className="relative z-10">
                                <h3 className="text-4xl font-black mb-6 font-serif text-white tracking-tight">Need immediate help?</h3>
                                <p className="text-purple-100/80 mb-12 font-sans text-lg leading-relaxed max-w-md mx-auto">Our support team is ready to help. Describe your issue in detail for faster resolution.</p>
                                <a href="mailto:support@bhrigugpt.com" className="group h-20 w-full sm:w-auto inline-flex items-center justify-center gap-6 px-16 bg-white text-gray-900 rounded-[32px] font-black font-sans uppercase tracking-[0.25em] text-sm hover:scale-[1.02] active:scale-[0.98] transition-colors shadow-2xl relative overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-100 to-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <Mail size={24} className="relative z-10" />
                                    <span className="relative z-10">Email Us Now</span>
                                </a>
                                <p className="mt-8 font-sans text-sm text-purple-100/70">
                                    Something not loading?{' '}
                                    <a
                                        href="https://bhrigugpt.hyperping.app"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-purple-300 font-bold hover:text-purple-100 transition-colors"
                                    >
                                        <Activity size={14} />
                                        Check system status
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
            <MobileBottomNav />
        </div>
    );
};

export default Contact;
